// The GIA ledger: the preferences rail filters the stones and writes the
// choice into the URL. With no diamond feed it filters the diamond selector's
// invented sample set (GIA-labelled examples only). In live mode
// (data-ledger-live) it searches the live stones and shows them as a grid of
// photos or a table, each linking to its stone page, and keeps the visitor's
// shortlist in a Comparison tab; the feed client loads only then, so demo
// mode never depends on it. The rail (snippets/diamond-ledger-filters.liquid)
// carries Rich's two quality presets, ranges with sliders, single grades and
// Advanced details, whose extra filters show only when the visitor asks.
import {
  DEFAULT_FILTERS,
  QUALITY_PRESETS,
  RANGE_LABELS,
  RANGE_LIMITS,
  applyQualityPreset,
  sampleDiamonds,
  filterDiamonds,
  readFilters,
  selectedQualityPreset,
  validateRanges,
  writeFilters,
} from "./diamond-model.js";
import {
  COMPARE_LIMIT,
  compared,
  comparisonHTML,
  heartHTML,
  onCompareChange,
  pressHearts,
  removeCompared,
  toggleCompared,
} from "./diamond-compare.js";
import "./diamond-help.js";

// A curated starting point, shown until the visitor changes anything.
const OPENING = {
  shapes: ["Round", "Oval"],
  caratMin: 1,
  caratMax: 2,
  sort: "carat-desc",
};
// The ranges on the rail, and the grade sets (field name: label).
const RANGES = ["carat", "length", "width", "height", "table", "depth", "ratio"];
const SETS = { shapes: "Shape", colours: "Colour", clarities: "Clarity", cuts: "Cut", polishes: "Polish", symmetries: "Symmetry", fluorescences: "Fluorescence" };
const CHOICES = { shade: "Shade", milky: "Milkiness", eyeClean: "Eye clean", treatment: "Treatment" };
// Which Advanced details panel holds each filter, so a filter in use is
// never hidden, and turning a panel off clears what it holds.
const PANEL = {
  length: "dimensions", width: "dimensions", height: "dimensions",
  table: "proportions", depth: "proportions", ratio: "proportions",
  polishes: "polish", symmetries: "symmetry", fluorescences: "fluorescence",
  shade: "shade", milky: "milky", eyeClean: "eyeClean", treatment: "treatment",
};
// Old shared links: "Excellent (3EX)" or "Very good or better" set one
// minimum for all three grades, and fluorescence was "None" or "None or faint".
const MINIMUM = {
  Excellent: { cuts: ["Excellent", "Ideal"], grades: ["Excellent"] },
  "Very good": { cuts: ["Very good", "Excellent", "Ideal"], grades: ["Very good", "Excellent"] },
};
const OLD_FLUOR = { "none-faint": ["None", "Faint"], None: ["None"] };
const escape = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
const number = (value) =>
  value === null || String(value).trim() === "" ? null : Number(value);
const active = (filters, key) =>
  RANGES.includes(key)
    ? filters[`${key}Min`] != null || filters[`${key}Max`] != null
    : Array.isArray(filters[key]) ? filters[key].length > 0 : Boolean(filters[key]);
// A run of colours as "D–F", otherwise a list.
const colourRun = (colours) => {
  const sorted = [...colours].sort();
  const run = sorted.every((c, i) => i === 0 || c.charCodeAt(0) === sorted[i - 1].charCodeAt(0) + 1);
  return run && sorted.length > 2 ? `${sorted[0]}–${sorted.at(-1)}` : sorted.join(", ");
};
const UNITS = { carat: " ct", length: " mm", width: " mm", height: " mm", table: "%", depth: "%", ratio: "" };
const rangeText = (filters, key) => `${filters[`${key}Min`] ?? "Any"}–${filters[`${key}Max`] ?? "any"}${UNITS[key]}`;
const CHOICE_TEXT = { "None or unknown": "none or not reported", Unknown: "not reported" };

// A slider runs 0–1000 over a range's limits: logarithmically for carat, so
// 1–2 ct isn't squeezed into a sliver of the track.
const toSlider = (value, { min, max, log }) =>
  Math.round(1000 * (log ? Math.log(value / min) / Math.log(max / min) : (value - min) / (max - min)));
const fromSlider = (position, { min, max, step, log }) => {
  const t = position / 1000;
  const raw = log ? min * (max / min) ** t : min + t * (max - min);
  const places = String(step).split(".")[1]?.length ?? 0;
  // Round to a tidy step for the size of the value.
  const tidy = log && raw >= 3 ? Math.round(raw * 10) / 10 : Number(raw.toFixed(places));
  return Math.min(max, Math.max(min, tidy));
};

class GiaLedger extends HTMLElement {
  connectedCallback() {
    if (this.controller || this.leaving) return;
    // An old link to a stone (?stone=) goes to its stone page before anything
    // is searched for; the page stays as it is if that can't load.
    if (this.hasAttribute("data-ledger-live")) {
      const leaving = leaveForStonePage();
      if (leaving) {
        this.leaving = true;
        leaving.then((left) => {
          this.leaving = left;
          if (!left && this.isConnected) this.connectedCallback();
        });
        return;
      }
    }
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector("[data-ledger-form]");
    this.rows = this.querySelector("[data-ledger-rows]");
    this.sortSelect = this.querySelector("[data-ledger-sort]");
    this.shapes = Object.fromEntries(
      [...this.querySelector("[data-ledger-shapes]").content.children].map(
        (node) => [node.dataset.shape, node.innerHTML],
      ),
    );
    this.railReset = this.querySelector("[data-ledger-reset]");
    this.railStatus = this.querySelector("[data-ledger-rail-status]");
    this.applyBtn = this.querySelector("[data-ledger-apply]");
    this.activeTags = this.querySelector("[data-ledger-active-tags]");
    this.mobileBar = this.querySelector("[data-ledger-mobile-bar]");
    this.mobileCount = this.querySelector("[data-ledger-mobile-count]");
    this.mobileStatus = this.querySelector("[data-ledger-mobile-status]");
    this.mobileApply = this.querySelector("[data-ledger-mobile-apply]");
    this.presetNote = this.querySelector("[data-preset-description]");
    this.presetIntro = this.presetNote?.textContent ?? "";
    this.advanced = this.querySelector("[data-advanced-details]");
    this.sliders = [...this.form.querySelectorAll("[data-diamond-range]")].map((fieldset) => ({
      key: fieldset.dataset.diamondRange,
      min: Number(fieldset.dataset.min),
      max: Number(fieldset.dataset.max),
      step: Number(fieldset.dataset.step),
      log: fieldset.dataset.scale === "log",
      track: fieldset.querySelector(".diamond-range__sliders"),
      ends: Object.fromEntries([...fieldset.querySelectorAll("[data-range-end]")].map((input) => [input.dataset.rangeEnd, input])),
    }));
    this.feed = this.hasAttribute("data-ledger-live")
      ? new LiveLedger(this, signal)
      : null;
    this.readURL();
    this.syncForm({ reveal: true });
    this.refresh({ now: true });
    this.updateActiveTags();
    this.querySelector("[data-ledger-fallback]").hidden = true;
    for (const slider of this.sliders) if (slider.track) slider.track.hidden = false;
    if (!this.feed) this.querySelector("[data-ledger-table]").hidden = false;
    // now: at once (Update results); otherwise after a wait, so a run of
    // quick edits makes one search: longer for a number stepped up or down
    // or a slider, where each click is a change.
    const apply = (now = false, wait = WAIT) => {
      this.readForm();
      this.refresh({ now, wait });
      this.writeURL();
    };
    if (this.applyBtn) {
      this.applyBtn.hidden = false;
      this.applyBtn.addEventListener(
        "click",
        () => {
          if (!this.applyBtn.hasAttribute("data-busy")) apply(true);
        },
        { signal },
      );
    }
    this.railReset?.addEventListener("click", () => this.clearAll(), { signal });
    this.activeTags?.addEventListener(
      "click",
      (event) => {
        const btn = event.target.closest("button");
        if (!btn) return;
        if (btn.hasAttribute("data-ledger-reset")) return this.clearAll();
        const tag = btn.dataset.clearTag;
        if (!tag) return;
        this.clearFilter(tag, btn.dataset.val);
        this.syncForm();
        this.refresh({ now: true });
        this.writeURL();
        this.focusResults();
      },
      { signal },
    );
    this.mobileApply?.addEventListener(
      "click",
      () => {
        this.querySelector(".ledger__results")?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
      },
      { signal },
    );
    this.querySelector(".diamond-presets")?.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[data-quality-preset]");
        if (button) this.choosePreset(button.dataset.qualityPreset);
      },
      { signal },
    );
    this.form.addEventListener(
      "input",
      (event) => {
        const end = event.target.closest("[data-range-end]");
        if (end) this.slide(end);
        else if (event.target.matches('input[type="number"]')) this.placeSliders();
        if (end || event.target.matches('input[type="number"]')) this.feed?.pending();
      },
      { signal },
    );
    this.form.addEventListener(
      "change",
      (event) => {
        const toggle = event.target.closest("[data-advanced-toggle]");
        if (toggle) {
          this.showPanel(toggle.dataset.advancedToggle, toggle.checked);
          // A panel turned off takes its filters with it; one turned on
          // changes nothing until something in it is chosen.
          if (toggle.checked || !this.clearPanel(toggle.dataset.advancedToggle)) return;
        }
        apply(false, event.target.matches('input[type="number"], [data-range-end]') ? RANGE_WAIT : WAIT);
      },
      { signal },
    );
    this.form.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        apply();
      },
      { signal },
    );
    this.sortSelect.addEventListener(
      "change",
      () => {
        this.filters.sort = this.sortSelect.value;
        this.refresh();
        this.writeURL();
      },
      { signal },
    );
    window.addEventListener(
      "popstate",
      () => {
        this.readURL();
        this.syncForm({ reveal: true });
        this.refresh({ now: true });
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  // Demo mode renders at once; live mode asks the feed.
  refresh({ now = false, wait = WAIT } = {}) {
    this.feed?.showTab("all");
    if (!this.feed) this.render();
    else if (now) this.feed.load();
    else this.feed.schedule(wait);
  }

  readURL() {
    const params = new URLSearchParams(location.search);
    const touched = [...params.keys()].some((key) => key.startsWith("ds_"));
    const filters = touched
      ? readFilters(location.search)
      : { ...structuredClone(DEFAULT_FILTERS), ...OPENING };
    // An old link's minimum grades and fluorescence become the sets.
    const minimum = MINIMUM[filters.cut] || MINIMUM[filters.polish];
    if (minimum) {
      if (!filters.cuts.length) filters.cuts = minimum.cuts;
      if (!filters.polishes.length) filters.polishes = minimum.grades;
      if (!filters.symmetries.length) filters.symmetries = minimum.grades;
    }
    const fluor = OLD_FLUOR[params.get("ds_fluor")];
    if (fluor && !filters.fluorescences.length) filters.fluorescences = fluor;
    filters.cut = filters.polish = filters.symmetry = filters.fluorescence = "";
    filters.lab = "GIA";
    this.filters = filters;
  }

  writeURL() {
    const url = writeFilters(this.filters, location.href);
    url.searchParams.delete("ds_lab");
    url.searchParams.delete("ds_fluor");
    url.searchParams.delete("ds_all");
    // A rail cleared to "anything" must not read as a fresh visit, which
    // would restore the opening preset on reload, back or a shared link.
    if (![...url.searchParams.keys()].some((key) => key.startsWith("ds_")))
      url.searchParams.set("ds_all", "1");
    history.replaceState(null, "", url);
  }

  readForm() {
    const data = new FormData(this.form);
    const filters = { ...structuredClone(DEFAULT_FILTERS), lab: "GIA" };
    for (const key of Object.keys(SETS)) filters[key] = data.getAll(key);
    for (const key of RANGES) {
      filters[`${key}Min`] = number(data.get(`${key}Min`));
      filters[`${key}Max`] = number(data.get(`${key}Max`));
    }
    for (const key of Object.keys(CHOICES)) filters[key] = data.get(key) || "";
    filters.sort = this.sortSelect.value;
    this.filters = filters;
  }

  // reveal: open the Advanced details panels whose filters are in use (a
  // fresh page, a preset, a shared link), so nothing chosen is out of sight.
  syncForm({ reveal = false } = {}) {
    const f = this.filters;
    for (const key of Object.keys(SETS))
      for (const input of this.form.querySelectorAll(`[name="${key}"]`))
        input.checked = f[key].includes(input.value);
    for (const key of RANGES) {
      const min = this.form.elements[`${key}Min`];
      const max = this.form.elements[`${key}Max`];
      if (min) min.value = f[`${key}Min`] ?? "";
      if (max) max.value = f[`${key}Max`] ?? "";
    }
    for (const key of Object.keys(CHOICES)) {
      const select = this.form.elements[key];
      if (select) select.value = f[key];
    }
    this.sortSelect.value = f.sort;
    this.placeSliders();
    if (reveal)
      for (const [key, panel] of Object.entries(PANEL))
        if (active(f, key)) this.showPanel(panel, true);
    this.pressPreset();
  }

  showPanel(name, on) {
    const panel = this.form.querySelector(`[data-advanced-panel="${name}"]`);
    const toggle = this.form.querySelector(`[data-advanced-toggle="${name}"]`);
    if (panel) panel.hidden = !on;
    if (toggle) toggle.checked = on;
    if (on && this.advanced) this.advanced.open = true;
  }

  // Clears a panel's filters; true if any was in use.
  clearPanel(name) {
    let cleared = false;
    for (const [key, panel] of Object.entries(PANEL)) {
      if (panel !== name || !active(this.filters, key)) continue;
      this.clearFilter(key);
      cleared = true;
    }
    if (cleared) this.syncForm();
    return cleared;
  }

  clearFilter(key, value) {
    const f = this.filters;
    if (key === "preset") {
      for (const name of Object.keys(QUALITY_PRESETS[value]?.filters ?? {})) this.clearFilter(name);
    } else if (key === "shapes") f.shapes = f.shapes.filter((s) => s !== value);
    else if (RANGES.includes(key)) f[`${key}Min`] = f[`${key}Max`] = null;
    else if (Array.isArray(f[key])) f[key] = [];
    else if (key in CHOICES) f[key] = "";
  }

  // A preset sets its grades; pressing the one in use takes them off.
  choosePreset(name) {
    if (!QUALITY_PRESETS[name]) return;
    if (selectedQualityPreset(this.filters) === name) {
      for (const key of Object.keys(QUALITY_PRESETS[name].filters)) this.clearFilter(key);
    } else {
      this.filters = applyQualityPreset(this.filters, name);
      for (const key of Object.keys(QUALITY_PRESETS[name].filters))
        if (PANEL[key] && active(this.filters, key)) this.showPanel(PANEL[key], true);
    }
    this.syncForm();
    this.refresh({ now: true });
    this.writeURL();
  }

  pressPreset() {
    const chosen = selectedQualityPreset(this.filters);
    for (const button of this.querySelectorAll("[data-quality-preset]"))
      button.setAttribute("aria-pressed", String(button.dataset.qualityPreset === chosen));
    if (this.presetNote)
      this.presetNote.textContent = chosen ? QUALITY_PRESETS[chosen].description : this.presetIntro;
  }

  // The sliders show the number fields' values, or the ends for "any".
  placeSliders() {
    for (const slider of this.sliders) {
      const { Min, Max } = slider.ends;
      if (!Min || !Max) continue;
      const read = (name, fallback) => {
        const value = number(this.form.elements[name]?.value);
        return Number.isFinite(value) ? Math.min(slider.max, Math.max(slider.min, value)) : fallback;
      };
      Min.value = toSlider(read(`${slider.key}Min`, slider.min), slider);
      Max.value = toSlider(read(`${slider.key}Max`, slider.max), slider);
      slider.track.style.setProperty("--from", `${Min.value / 10}%`);
      slider.track.style.setProperty("--to", `${Max.value / 10}%`);
    }
  }

  // A slider moved: its number field follows, an end at the limit reads
  // "any", and the two ends never cross.
  slide(end) {
    const slider = this.sliders.find((s) => s.ends.Min === end || s.ends.Max === end);
    if (!slider) return;
    const { Min, Max } = slider.ends;
    if (Number(Min.value) > Number(Max.value)) {
      if (end === Min) Min.value = Max.value;
      else Max.value = Min.value;
    }
    const field = (name, position, limit) => {
      const input = this.form.elements[name];
      if (input) input.value = Number(position) === limit ? "" : fromSlider(Number(position), slider);
    };
    field(`${slider.key}Min`, Min.value, 0);
    field(`${slider.key}Max`, Max.value, 1000);
    slider.track.style.setProperty("--from", `${Min.value / 10}%`);
    slider.track.style.setProperty("--to", `${Max.value / 10}%`);
  }

  summary() {
    const f = this.filters;
    const parts = [f.shapes.join(", ") || "Any shape"];
    for (const key of RANGES) if (active(f, key)) parts.push(key === "carat" ? rangeText(f, key) : `${RANGE_LABELS[key]} ${rangeText(f, key)}`);
    const preset = selectedQualityPreset(f);
    if (preset) parts.push(QUALITY_PRESETS[preset].label);
    else {
      if (f.colours.length) parts.push(colourRun(f.colours));
      for (const key of ["clarities", "cuts", "polishes", "symmetries"]) if (f[key].length) parts.push(`${SETS[key]} ${f[key].join(", ")}`);
      if (f.fluorescences.length) parts.push(`Fluorescence ${f.fluorescences.join(", ").toLowerCase()}`);
      for (const [key, label] of Object.entries(CHOICES)) if (f[key]) parts.push(`${label} ${CHOICE_TEXT[f[key]] ?? f[key].toLowerCase()}`);
    }
    return parts.join(" · ");
  }

  render() {
    const stones = filterDiamonds(sampleDiamonds, this.filters);
    this.rows.innerHTML = stones
      .map(
        (d) => `<tr>
  <td>${this.shapes[d.shape]}</td>
  <td class="ledger__shape">${escape(d.shape)}</td>
  <td>${d.carat.toFixed(2)} ct</td>
  <td>${escape(d.colour)}</td>
  <td>${escape(d.clarity)}</td>
  <td>${escape(d.cut ?? "—")}</td>
  <td>${escape(d.fluorescence)}</td>
  <td class="ledger__mm">${d.length.toFixed(2)} × ${d.width.toFixed(2)} × ${d.height.toFixed(2)} mm</td>
  <td class="ledger__lab">GIA</td>
  <td><a class="button" href="/pages/contact?interest=diamond&amp;shape=${encodeURIComponent(d.shape)}">Inquire</a></td>
</tr>`,
      )
      .join("");
    this.querySelector("[data-ledger-count]").innerHTML =
      `${stones.length} example stone${stones.length === 1 ? "" : "s"} match · <em>${escape(this.summary())}</em>`;
    this.querySelector("[data-ledger-empty]").hidden = stones.length > 0;
    this.querySelector("[data-ledger-table]").hidden = stones.length === 0;
    this.updateActiveTags();
  }

  clearAll() {
    this.filters = { ...structuredClone(DEFAULT_FILTERS), lab: "GIA" };
    this.syncForm();
    this.refresh({ now: true });
    this.writeURL();
    this.focusResults();
  }

  focusResults() {
    const count = this.querySelector("[data-ledger-count]");
    count?.setAttribute("tabindex", "-1");
    count?.focus({ preventScroll: true });
  }

  updateActiveTags() {
    if (!this.activeTags) return;
    const f = this.filters;
    const pill = (tag, label, value = "") =>
      `<button type="button" class="ledger__tag-pill" data-clear-tag="${tag}"${value ? ` data-val="${escape(value)}"` : ""}>${escape(label)} <span class="ledger__tag-x" aria-hidden="true">×</span><span class="visually-hidden">Remove ${escape(label)} filter</span></button>`;
    const tags = f.shapes.map((shape) => pill("shapes", shape, shape));
    for (const key of RANGES) if (active(f, key)) tags.push(pill(key, key === "carat" ? rangeText(f, key) : `${RANGE_LABELS[key].replace(/ \(mm\)$/, "")}: ${rangeText(f, key)}`));
    const preset = selectedQualityPreset(f);
    const inPreset = new Set(preset ? Object.keys(QUALITY_PRESETS[preset].filters) : []);
    if (preset) tags.push(pill("preset", QUALITY_PRESETS[preset].label, preset));
    if (f.colours.length && !inPreset.has("colours")) tags.push(pill("colours", `Colour: ${colourRun(f.colours)}`));
    for (const key of ["clarities", "cuts", "polishes", "symmetries", "fluorescences"])
      if (f[key].length && !inPreset.has(key)) tags.push(pill(key, `${SETS[key]}: ${f[key].join(", ")}`));
    for (const [key, label] of Object.entries(CHOICES))
      if (f[key] && !inPreset.has(key)) tags.push(pill(key, `${label}: ${CHOICE_TEXT[f[key]] ?? f[key].toLowerCase()}`));
    if (tags.length) {
      tags.push(`<button type="button" class="ledger__tag-clear" data-ledger-reset>Clear all</button>`);
      this.activeTags.innerHTML = tags.join("");
      this.activeTags.hidden = false;
      if (this.railReset) this.railReset.hidden = false;
    } else {
      this.activeTags.innerHTML = "";
      this.activeTags.hidden = true;
      if (this.railReset) this.railReset.hidden = true;
    }
  }
}

// Live mode: the stones and the count come from the diamond feed, as a grid of
// photos (the default) or a table; the visitor's choice is remembered. Each
// stone links to its stone page. A change waits a moment, cancels the request
// before it and shows only the answer for the latest filters. A failure says
// so and never falls back to sample stones.
const WAIT = 400; // after a choice, before the search
const RANGE_WAIT = 800; // after a number or slider, which change step by step
const SLOW = 4000; // after this, the loading state says it is still at work
const EAGER = 8; // the first photos load at once; the first half of them first
const RETRIES = 2; // empty pages with more to come passed over before "no stones"
const LAYOUT = "gia-ledger-layout";
const REPORT_HOSTS = ["https://www.gia.edu/", "https://www.igi.org/"];
const reportURL = (value) =>
  typeof value === "string" && REPORT_HOSTS.some((host) => value.startsWith(host))
    ? value
    : "";
// Shown if the live modules themselves can't load: the same words as
// feedFailureHTML("unavailable") in diamond-feed.js.
const LOAD_FAILURE = `<p class="feed-failure">The live stones can't be loaded right now. Tell Rich what you're after and he'll search for you. <a href="/pages/contact?interest=diamond">Talk to Rich</a> or <a href="/pages/diamond-guide">read the diamond guide</a>.</p>`;
let modules = null;
const liveModules = () => (modules ??= import("./diamond-feed.js"));
// An old link to a stone on this page (?stone=X) now goes to the stone page,
// which leads back here without it. null: no such link; otherwise a promise
// of whether the page is leaving.
const leaveForStonePage = () => {
  const params = new URLSearchParams(location.search);
  const id = params.get("stone");
  if (!id || params.has("view")) return null;
  params.delete("stone");
  const query = params.toString();
  const from = location.pathname + (query ? `?${query}` : "");
  return liveModules().then(
    ({ stonePageURL }) => {
      location.replace(stonePageURL(id, { from }));
      return true;
    },
    () => false,
  );
};
// The visitor's last choice of grid or table; the grid unless they chose the
// table. Storage may be refused (private modes, embeds), so both ways are guarded.
const storedLayout = () => {
  try {
    return localStorage.getItem(LAYOUT) === "table" ? "table" : "grid";
  } catch {
    return "grid";
  }
};
const storeLayout = (layout) => {
  try {
    localStorage.setItem(LAYOUT, layout);
  } catch {
    /* The choice still holds on this page. */
  }
};
// Placeholders shaped like what is coming: eight cards, or six rows.
const bar = (kind) => `<span class="skeleton skeleton-bar skeleton-bar--${kind}"></span>`;
const LOADING_INTRO = `<div class="ledger__loading-intro"><svg class="ledger__loading-gem" viewBox="0 0 64 56" width="48" height="42" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><path d="M14 5h36l10 15-28 31L4 20Z"/><path d="M4 20h56M14 5l8 15L32 5l10 15 8-15M22 20l10 31 10-31"/></svg><div><p class="ledger__loading-title">Finding your diamond</p><p class="ledger__loading-copy">Matching your preferences with Rich’s suppliers.</p></div></div>`;
const SKELETON = {
  grid: `${LOADING_INTRO}<ul class="stone-grid">${`<li class="stone-card stone-card--skeleton"><span class="stone-card__media skeleton"></span>${bar("line")}${bar("short")}${bar("price")}</li>`.repeat(8)}</ul><p class="loading-label">Loading…</p>`,
  table: `${LOADING_INTRO}${`<div class="ledger__skeleton-row"><span class="skeleton ledger__skeleton-shape"></span>${bar("line")}${bar("short")}${bar("price")}</div>`.repeat(6)}<p class="loading-label">Loading…</p>`,
};
// A carat range the feed would refuse, said calmly instead of asking.
const rangeMessage = ([error]) => {
  const key = error.field.replace(/(Min|Max)$/, "");
  const [low, high] = RANGE_LIMITS[key];
  const name = key === "carat" ? "carat" : RANGE_LABELS[key].toLowerCase();
  return /maximum needs/.test(error.message)
    ? `Check the ${name} range: the maximum needs to be at least the minimum.`
    : `Check the ${name} range: it must be between ${low} and ${high}.`;
};
// The line above the stones. count: the feed's total, or null if it couldn't
// be had (the stones still show). With a budget it is approximate: the count
// can include stones the search leaves out at the budget's edge.
const countText = ({ count, approximate }, shown, more) => {
  if (!shown && !more) return "0 stones match";
  if (count === null)
    return `Showing ${shown.toLocaleString("en-GB")} ${shown === 1 ? "stone" : "stones"}`;
  const total = Math.max(count, shown);
  return `${approximate ? "About " : ""}${total.toLocaleString("en-GB")} ${total === 1 ? "stone matches" : "stones match"}`;
};
// The count is a nicety: if it fails the stones still show, unless the
// visitor's own abort stopped it.
const optional = (counting) =>
  counting.catch((error) => {
    if (error?.name === "AbortError") throw error;
    return null;
  });

class LiveLedger {
  constructor(ledger, signal) {
    this.ledger = ledger;
    this.live = null; // the feed client, once loaded
    this.base = "";
    this.page = 1;
    this.counted = null;
    this.stones = []; // every stone shown, in order
    this.layout = storedLayout();
    this.list = ledger.querySelector("[data-ledger-list]");
    this.table = ledger.querySelector("[data-ledger-table]");
    this.grid = ledger.querySelector("[data-ledger-grid]");
    this.skeleton = ledger.querySelector("[data-ledger-skeleton]");
    this.slow = ledger.querySelector("[data-ledger-slow]");
    this.progress = ledger.querySelector("[data-ledger-bar]");
    this.switch = ledger.querySelector("[data-ledger-switch]");
    this.count = ledger.querySelector("[data-ledger-count]");
    this.empty = ledger.querySelector("[data-ledger-empty]");
    this.error = ledger.querySelector("[data-ledger-error]");
    this.more = ledger.querySelector("[data-ledger-more]");
    this.moreLabel = this.more.textContent;
    this.railStatus = ledger.querySelector("[data-ledger-rail-status]");
    this.applyBtn = ledger.querySelector("[data-ledger-apply]");
    this.mobileBar = ledger.querySelector("[data-ledger-mobile-bar]");
    this.mobileCount = ledger.querySelector("[data-ledger-mobile-count]");
    this.mobileStatus = ledger.querySelector("[data-ledger-mobile-status]");
    // The colours the ledger offers, its buttons (D to K). With none chosen
    // these are asked for, so it never lists a colour it doesn't offer.
    this.colours = [...ledger.form.querySelectorAll('[name="colours"]')].map((input) => input.value);
    this.inView = true;
    if (this.mobileBar && typeof IntersectionObserver !== "undefined") {
      const observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.mobileBar.hidden = !entry.isIntersecting || this.counted === null;
      });
      observer.observe(ledger);
      signal.addEventListener("abort", () => observer.disconnect(), { once: true });
    }
    this.ready = liveModules().then(
      (live) => {
        this.live = live;
        this.base = live.feedBase(document);
      },
      () => {},
    );
    this.switch.hidden = false;
    this.pressLayout();
    this.switch.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[data-ledger-layout]");
        if (button) this.setLayout(button.dataset.ledgerLayout);
      },
      { signal },
    );
    // A photo fades in once it has loaded; one that fails leaves the outline.
    this.grid.addEventListener(
      "load",
      (event) => {
        if (event.target.matches?.(".stone-card__media img")) event.target.classList.add("is-loaded");
      },
      { capture: true, signal },
    );
    this.grid.addEventListener(
      "error",
      (event) => {
        if (!event.target.matches?.(".stone-card__media img")) return;
        event.target.closest(".stone-card__media").classList.add("stone-card__media--outline");
        event.target.remove();
      },
      { capture: true, signal },
    );
    this.more.addEventListener(
      "click",
      () => {
        if (!this.more.hasAttribute("aria-disabled")) this.load(this.page + 1);
      },
      { signal },
    );
    signal.addEventListener("abort", () => this.cancel());
    // The shortlist: a heart on each card and row, and the Comparison tab.
    this.tabs = [...ledger.querySelectorAll("[data-diamond-tab]")];
    this.allPanel = ledger.querySelector("[data-diamond-all-panel]");
    this.comparePanel = ledger.querySelector("[data-comparison-panel]");
    this.compareCount = ledger.querySelector("[data-compare-count]");
    this.tab = "all";
    const tablist = this.tabs[0]?.parentElement;
    tablist?.addEventListener(
      "click",
      (event) => {
        const tab = event.target.closest("[data-diamond-tab]");
        if (tab) this.showTab(tab.dataset.diamondTab);
      },
      { signal },
    );
    // Arrow keys move between the tabs, as a tab list should.
    tablist?.addEventListener(
      "keydown",
      (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        const at = this.tabs.indexOf(document.activeElement);
        if (at < 0) return;
        event.preventDefault();
        const next = event.key === "Home" ? 0 : event.key === "End" ? this.tabs.length - 1 : (at + (event.key === "ArrowRight" ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.showTab(this.tabs[next].dataset.diamondTab);
        this.tabs[next].focus();
      },
      { signal },
    );
    this.list.addEventListener(
      "click",
      (event) => {
        const heart = event.target.closest("[data-compare-toggle]");
        if (!heart) return;
        const stone = this.stones.find((s) => String(s.id) === heart.dataset.compareToggle);
        if (stone && toggleCompared(stone) === "full")
          this.notice(`You can compare up to ${COMPARE_LIMIT} diamonds. Remove one in the Comparison tab to add another.`);
      },
      { signal },
    );
    this.comparePanel?.addEventListener(
      "click",
      (event) => {
        const remove = event.target.closest("[data-compare-remove]");
        if (!remove) return;
        removeCompared(remove.dataset.compareRemove);
        // Focus stays in the panel: on the next stone's Remove, or the tab.
        (this.comparePanel.querySelector("[data-compare-remove]") ?? this.tabs[1])?.focus();
      },
      { signal },
    );
    this.comparePanel?.addEventListener(
      "error",
      (event) => {
        const media = event.target.closest?.(".compare__media");
        if (!media || event.target.tagName !== "IMG") return;
        media.classList.add("compare__media--outline");
        media.innerHTML = this.outline({ shape: media.dataset.shape });
      },
      { capture: true, signal },
    );
    onCompareChange(() => this.compareChanged(), signal);
    this.compareChanged();
  }

  showTab(name) {
    if (!this.comparePanel || (name !== "all" && name !== "compare") || name === this.tab) return;
    this.tab = name;
    for (const tab of this.tabs) {
      const on = tab.dataset.diamondTab === name;
      tab.setAttribute("aria-selected", String(on));
      tab.tabIndex = on ? 0 : -1;
    }
    this.allPanel.hidden = name !== "all";
    this.comparePanel.hidden = name !== "compare";
    if (name === "compare") this.drawComparison();
  }

  compareChanged() {
    const n = compared().length;
    if (this.compareCount) this.compareCount.textContent = `(${n})`;
    pressHearts(this.list);
    if (this.tab === "compare") this.drawComparison();
  }

  // The Comparison tab needs the feed client for titles, prices and links.
  async drawComparison() {
    await this.ready;
    if (!this.comparePanel || this.tab !== "compare") return;
    if (!this.live) {
      this.comparePanel.innerHTML = LOAD_FAILURE;
      return;
    }
    this.comparePanel.innerHTML = comparisonHTML(compared(), this.live, {
      outline: (shape) => this.outline({ shape }),
      from: location.pathname + location.search,
    });
  }

  // A passing message, read out too.
  notice(text) {
    this.count.textContent = text;
    this.count.hidden = false;
    clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => {
      if (this.counted) this.count.innerHTML = `${countText(this.counted, this.stones.length, !this.more.hidden)} · <em>${escape(this.counted.summary)}</em>`;
    }, 5000);
  }

  pressLayout() {
    for (const button of this.switch.querySelectorAll("[data-ledger-layout]"))
      button.setAttribute("aria-pressed", String(button.dataset.ledgerLayout === this.layout));
  }

  // Grid or table: the stones already shown are drawn again in the other.
  setLayout(layout) {
    if (layout !== "grid" && layout !== "table") return;
    storeLayout(layout);
    if (layout === this.layout) return;
    this.layout = layout;
    this.pressLayout();
    if (!this.skeleton.hidden) this.skeleton.innerHTML = SKELETON[layout];
    this.ledger.rows.innerHTML = "";
    this.grid.innerHTML = "";
    this.draw(this.stones, 0);
    this.reveal();
  }

  // The stones from index `from` on, into the layout in use.
  draw(stones, from) {
    if (this.layout === "grid") {
      this.grid.insertAdjacentHTML("beforeend", stones.map((stone, i) => this.card(stone, from + i)).join(""));
      // A photo the browser already holds may have loaded before the listener saw it.
      for (const img of this.grid.querySelectorAll(".stone-card__media img:not(.is-loaded)"))
        if (img.complete && img.naturalWidth) img.classList.add("is-loaded");
    } else {
      this.ledger.rows.insertAdjacentHTML("beforeend", stones.map((stone) => this.row(stone)).join(""));
    }
  }

  // Shows the layout in use if there are stones, and neither if not.
  reveal() {
    const any = this.stones.length > 0;
    this.grid.hidden = !(any && this.layout === "grid");
    this.table.hidden = !(any && this.layout === "table");
  }

  cancel() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.request = null;
    this.moreBusy(false);
  }

  // A search after a wait; each new change restarts it. A range the feed
  // would refuse is said at once, with nothing to wait for.
  schedule(wait = WAIT) {
    this.cancel();
    const errors = validateRanges(this.offered(this.ledger.filters));
    this.markRanges(errors);
    if (errors.length) return this.rangeProblem(errors);
    this.more.hidden = true;
    this.busy();
    this.timer = setTimeout(() => this.load(), wait);
  }

  pending() {
    if (this.railStatus) {
      this.railStatus.hidden = false;
      const text = this.railStatus.querySelector("span:not(.spinner)");
      if (text) text.textContent = "Ready to update";
      const spinner = this.railStatus.querySelector(".spinner");
      if (spinner) spinner.hidden = true;
    }
    if (this.applyBtn) {
      this.applyBtn.classList.add("ledger__apply--pending");
    }
  }

  // A new search is on its way, and the count says so. Stones already shown
  // stay, dimmed, under a thin moving bar; with none shown, placeholders
  // stand in. After a while with no answer, a line says it is still at work.
  busy() {
    this.list.setAttribute("aria-busy", "true");
    this.count.textContent = "Looking for live stones…";
    this.count.hidden = false;
    if (this.railStatus) {
      this.railStatus.hidden = false;
      const text = this.railStatus.querySelector("span:not(.spinner)");
      if (text) text.textContent = "Updating…";
      const spinner = this.railStatus.querySelector(".spinner");
      if (spinner) spinner.hidden = false;
    }
    if (this.applyBtn) {
      this.applyBtn.setAttribute("data-busy", "true");
      this.applyBtn.setAttribute("aria-disabled", "true");
      this.applyBtn.classList.remove("ledger__apply--pending");
      const label = this.applyBtn.querySelector(".ledger__apply-label");
      if (label) label.textContent = "Updating…";
      const sp = this.applyBtn.querySelector(".spinner");
      if (sp) sp.hidden = false;
    }
    if (this.mobileStatus) this.mobileStatus.textContent = "Searching…";
    if (this.stones.length) this.progress.hidden = false;
    else if (this.skeleton.hidden) {
      this.skeleton.innerHTML = SKELETON[this.layout];
      this.skeleton.hidden = false;
      this.empty.hidden = true;
      this.error.hidden = true;
    }
    this.slowTimer ??= setTimeout(() => {
      this.slow.hidden = false;
    }, SLOW);
  }

  // The search has answered, failed or been set aside.
  idle() {
    this.list.removeAttribute("aria-busy");
    this.progress.hidden = true;
    this.skeleton.hidden = true;
    this.skeleton.innerHTML = "";
    if (this.railStatus) this.railStatus.hidden = true;
    if (this.applyBtn) {
      this.applyBtn.removeAttribute("data-busy");
      this.applyBtn.removeAttribute("aria-disabled");
      this.applyBtn.classList.remove("ledger__apply--pending");
      const label = this.applyBtn.querySelector(".ledger__apply-label");
      if (label) label.textContent = "Update results";
      const sp = this.applyBtn.querySelector(".spinner");
      if (sp) sp.hidden = true;
    }
    this.ledger.updateActiveTags();
    if (this.mobileBar) this.mobileBar.hidden = !this.inView;
    clearTimeout(this.slowTimer);
    this.slowTimer = null;
    this.slow.hidden = true;
  }

  // Show more while its page loads: a spinner, and it can't be pressed twice.
  moreBusy(on) {
    if (on) {
      this.more.setAttribute("aria-disabled", "true");
      this.more.innerHTML = `<span class="spinner" aria-hidden="true"></span>Loading more stones…`;
    } else if (this.more.hasAttribute("aria-disabled")) {
      this.more.removeAttribute("aria-disabled");
      this.more.textContent = this.moreLabel;
    }
  }

  // The visitor's filters, within the colours the ledger offers.
  offered(filters) {
    if (filters.colours.length || !this.colours.length) return filters;
    return { ...filters, colours: this.colours };
  }

  async load(page = 1) {
    this.cancel();
    const filters = this.offered(this.ledger.filters);
    // A range the feed would refuse is said here, and nothing is asked.
    const errors = validateRanges(filters);
    this.markRanges(errors);
    if (errors.length) return this.rangeProblem(errors);
    const request = (this.request = new AbortController());
    const options = { signal: request.signal };
    const summary = this.ledger.summary();
    const approximate = filters.priceMin !== null || filters.priceMax !== null;
    const fresh = page === 1;
    if (fresh) {
      this.more.hidden = true;
      this.busy();
    } else this.moreBusy(true);
    try {
      await this.ready;
      if (request !== this.request) return;
      const live = this.live;
      if (!live) return this.fail(null, !fresh);
      if (!this.base) throw new live.FeedError("unavailable");
      let [found, count] = await Promise.all([
        live.searchStones(this.base, filters, { ...options, page }),
        fresh ? optional(live.countStones(this.base, filters, options)) : null,
      ]);
      // A page the feed emptied with more to come is passed over, twice at
      // most. If the stones are still none, there are none to show.
      for (let tries = 0; !found.stones.length && found.more && tries < RETRIES; tries++) {
        if (request !== this.request) return;
        page += 1;
        found = await live.searchStones(this.base, filters, { ...options, page });
      }
      if (request !== this.request) return;
      if (!found.stones.length && fresh) found = { ...found, more: false };
      this.page = page;
      this.show(found, fresh ? { count, summary, approximate } : null);
    } catch (error) {
      if (request !== this.request || error?.name === "AbortError") return;
      this.fail(error, !fresh);
    } finally {
      if (request === this.request) {
        this.moreBusy(false);
        this.idle();
      }
    }
  }

  markRanges(errors) {
    for (const input of this.ledger.form.querySelectorAll('[name$="Min"], [name$="Max"]'))
      if (errors.some((error) => error.field === input.name))
        input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
  }

  clear() {
    this.stones = [];
    this.ledger.rows.innerHTML = "";
    this.grid.innerHTML = "";
    this.reveal();
  }

  rangeProblem(errors) {
    this.idle();
    this.clear();
    this.counted = null;
    this.count.textContent = rangeMessage(errors);
    if (this.mobileCount) this.mobileCount.textContent = "Check your filters";
    if (this.mobileStatus) this.mobileStatus.textContent = rangeMessage(errors);
    this.count.hidden = false;
    this.more.hidden = true;
    this.error.hidden = true;
    this.empty.hidden = true;
  }

  show({ stones, more }, fresh) {
    const before = fresh ? 0 : this.stones.length;
    if (fresh) {
      this.ledger.rows.innerHTML = "";
      this.grid.innerHTML = "";
      this.stones = stones;
      this.counted = fresh;
    } else this.stones = this.stones.concat(stones);
    this.draw(stones, before);
    const shown = this.stones.length;
    // Never fewer than the stones shown; "Showing N" if the count failed.
    if (this.counted)
      this.count.innerHTML = `${countText(this.counted, shown, more)} · <em>${escape(this.counted.summary)}</em>`;
    if (this.mobileCount) {
      this.mobileCount.textContent = countText(this.counted ?? { count: null }, shown, more);
    }
    if (this.mobileStatus) this.mobileStatus.textContent = "Matches for your preferences";
    this.error.hidden = true;
    this.count.hidden = false;
    this.reveal();
    this.empty.hidden = shown > 0;
    // Keep keyboard focus in the list when Show more goes away.
    const focused = document.activeElement === this.more;
    this.more.hidden = !more;
    if (!fresh && focused && !more) {
      const items = this.layout === "grid" ? this.grid.children : this.ledger.rows.children;
      items[before]?.querySelector("[data-ledger-view]")?.focus();
    }
  }

  // error null: the live modules didn't load.
  fail(error, appending) {
    const live = this.live;
    const kind = live && error instanceof live.FeedError && error.kind === "busy" ? "busy" : "unavailable";
    this.error.innerHTML = live ? live.feedFailureHTML(kind) : LOAD_FAILURE;
    this.error.hidden = false;
    this.empty.hidden = true;
    // A next page that failed leaves the stones shown, and Show more to try
    // again.
    this.more.hidden = !appending;
    if (appending) return;
    this.idle();
    this.clear();
    this.count.hidden = true;
    if (this.mobileCount) this.mobileCount.textContent = "Search unavailable";
    if (this.mobileStatus) this.mobileStatus.textContent = "Try again or talk to Rich";
  }

  price(stone) {
    try {
      return this.live.formatPrice(stone);
    } catch {
      return "—";
    }
  }

  // The stone's page, leading back to these results.
  link(stone) {
    return this.live.stonePageURL(stone.id, { from: location.pathname + location.search });
  }

  outline(stone) {
    const shapes = this.ledger.shapes;
    return Object.hasOwn(shapes, stone.shape) ? shapes[stone.shape] : "";
  }

  // One card: a link to the stone's page, centred and compact (title, grades,
  // price; the report number is on the stone page), with the comparison heart
  // beside the link, not in it. index: its place in the list; the first
  // photos load at once (the first four first) and the rest as they near the
  // screen.
  card(stone, index) {
    const { stoneTitle, treatmentLabel, feedURL } = this.live;
    const photo = feedURL(stone.image, "image");
    const priority = index < EAGER ? (index < EAGER / 2 ? ' fetchpriority="high"' : "") : ' loading="lazy"';
    const grades = [stone.colour, stone.clarity, stone.cut].filter(Boolean).join(" · ");
    const treated = treatmentLabel(stone.treatment);
    const title = stoneTitle(stone);
    return `<li class="stone-card"><a class="stone-card__link" href="${escape(this.link(stone))}" data-ledger-view="${escape(stone.id)}">
  <span class="stone-card__media${photo ? "" : " stone-card__media--outline"}"><span class="stone-card__badge">${escape([stone.origin || "Natural", stone.lab].filter(Boolean).join(" · "))}</span><span class="stone-card__outline" aria-hidden="true">${this.outline(stone)}</span>${photo ? `<img src="${escape(photo)}" alt="" width="600" height="600"${priority} decoding="async">` : ""}</span>
  <span class="stone-card__title">${escape(title)}</span>
  <span class="stone-card__grades">${escape(grades)}</span>
  <span class="stone-card__price">${escape(this.price(stone))}</span>${treated ? `
  <span class="stone-card__treatment">${escape(treated)}</span>` : ""}
</a>${heartHTML(stone, title)}</li>`;
  }

  row(stone) {
    const { stoneTitle, treatmentLabel } = this.live;
    const size = [stone.length, stone.width, stone.height].every(Number.isFinite)
      ? `${stone.length.toFixed(2)} × ${stone.width.toFixed(2)} × ${stone.height.toFixed(2)} mm`
      : "—";
    const report = escape([stone.lab, stone.id].filter(Boolean).join(" "));
    const link = reportURL(stone.reportUrl);
    const treated = treatmentLabel(stone.treatment);
    return `<tr>
  <td>${this.outline(stone)}</td>
  <td class="ledger__shape">${escape(stone.shape)}${treated ? `<small class="ledger__treatment">${escape(treated)}</small>` : ""}</td>
  <td>${Number(stone.carat).toFixed(2)}&nbsp;ct</td>
  <td>${escape(stone.colour)}</td>
  <td>${escape(stone.clarity)}</td>
  <td>${escape(stone.cut || "—")}</td>
  <td>${escape(stone.fluorescence || "—")}</td>
  <td class="ledger__mm">${size}</td>
  <td class="ledger__lab">${link ? `<a href="${escape(link)}" target="_blank" rel="noopener">${report}</a>` : report}</td>
  <td class="ledger__price">${escape(this.price(stone))}</td>
  <td><span class="ledger__row-actions"><a class="button" href="${escape(this.link(stone))}" data-ledger-view="${escape(stone.id)}">View<span class="visually-hidden"> ${escape(stoneTitle(stone))}</span></a>${heartHTML(stone, stoneTitle(stone))}</span></td>
</tr>`;
  }
}

if (!customElements.get("gia-ledger"))
  customElements.define("gia-ledger", GiaLedger);
